import React, { useEffect, useState } from "react";
import AdminSidebar from "../components/AdminSidebar";
import AddressAutocomplete from "../components/AddressAutocomplete";
import api, { API } from "../lib/api";
import { toast } from "sonner";
import { Save, Info, PawPrint, Link2, Copy, Upload, ExternalLink, Sparkles, EyeOff, ImageIcon, Crown, Check, X, RefreshCw, Loader2, Wheat, MapPin, FileText } from "lucide-react";

export default function AdminSettings() {
    const [form, setForm] = useState({
        name: "", owner_email: "", phone: "", address: "", city: "", lat: null, lng: null,
        opening_hours: "", description: "",
        pets_allowed: false, gluten_free: false,
    });
    const [slug, setSlug] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/admin/restaurant").then((r) => {
            setForm({
                name: r.data.name || "",
                owner_email: r.data.owner_email || "",
                phone: r.data.phone || "",
                address: r.data.address || "",
                city: r.data.city || "",
                lat: r.data.lat ?? null,
                lng: r.data.lng ?? null,
                opening_hours: r.data.opening_hours || "",
                description: r.data.description || "",
                pets_allowed: !!r.data.pets_allowed,
                gluten_free: !!r.data.gluten_free,
            });
            setSlug(r.data.slug || "");
        }).catch(() => {});
    }, []);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put("/admin/restaurant", form);
            toast.success("Impostazioni salvate");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="flex min-h-screen bg-ink" data-testid="admin-settings-page">
            <AdminSidebar />
            <main className="flex-1 p-10">
                <div className="mb-8">
                    <div className="text-gold text-xs tracking-[0.4em] uppercase">Configurazione</div>
                    <h1 className="font-serif text-5xl text-white mt-2">Impostazioni</h1>
                    <p className="text-gray-400 text-sm mt-2">I dati del tuo ristorante. L'email riceverà le notifiche di prenotazione.</p>
                </div>

                {slug && (
                    <div className="mb-6 max-w-3xl bg-gold/5 border border-gold/20 rounded-sm p-4 flex items-start gap-3">
                        <Info size={16} className="text-gold mt-0.5 flex-shrink-0" strokeWidth={1.5} />
                        <div className="text-xs text-gray-300">
                            La pagina pubblica del tuo ristorante è: <code className="text-gold">{window.location.origin}/r/{slug}</code>
                            <br />
                            Per cambiare lo slug/URL, contatta Renova.
                        </div>
                    </div>
                )}

                <form onSubmit={submit} className="bg-ink-surface border border-white/10 rounded-sm p-8 max-w-3xl space-y-5">
                    <Field label="Nome del ristorante">
                        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                            required data-testid="settings-name-input"
                            className="w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none" />
                    </Field>
                    <Field label="Email (destinataria notifiche)">
                        <input type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
                            required data-testid="settings-email-input"
                            className="w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none" />
                    </Field>
                    <Field label="Telefono">
                        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            data-testid="settings-phone-input"
                            className="w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none" />
                    </Field>
                    <Field label="Indirizzo (geolocalizzato)">
                        <AddressAutocomplete
                            value={form.address}
                            locked={!!(form.lat && form.lng)}
                            onChange={(v) => setForm({ ...form, address: v, lat: null, lng: null })}
                            onSelect={({ address, city, lat, lng }) => {
                                setForm((f) => ({ ...f, address, city, lat, lng }));
                                toast.success(`Indirizzo confermato${city ? ` · ${city}` : ""}`);
                            }}
                            testid="settings-address"
                        />
                        {form.city && (
                            <div className="mt-1.5 text-[10px] uppercase tracking-widest text-emerald-300" data-testid="settings-city-label">
                                Città riconosciuta: <span className="text-white font-semibold">{form.city}</span>
                                {form.lat && form.lng && (
                                    <span className="text-gray-500 ml-2 normal-case tracking-normal">
                                        ({form.lat.toFixed(5)}, {form.lng.toFixed(5)})
                                    </span>
                                )}
                            </div>
                        )}
                    </Field>
                    <Field label="Orari di apertura">
                        <input value={form.opening_hours} onChange={(e) => setForm({ ...form, opening_hours: e.target.value })}
                            data-testid="settings-hours-input"
                            className="w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none" />
                    </Field>
                    <Field label="Descrizione">
                        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                            rows={4} data-testid="settings-description-input"
                            className="w-full px-3 py-2.5 bg-transparent border border-white/20 rounded-sm text-white focus:border-gold focus:outline-none resize-none" />
                    </Field>

                    <div className="border-t border-white/10 pt-5">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold mb-3">Servizi</div>
                        <label className="flex items-center justify-between gap-4 border border-white/10 rounded-sm px-4 py-3 cursor-pointer hover:border-gold/40" data-testid="settings-pets-toggle">
                            <div className="flex items-center gap-3">
                                <PawPrint size={20} className={form.pets_allowed ? "text-emerald-300" : "text-gray-500"} strokeWidth={1.5} />
                                <div>
                                    <div className="text-white text-sm font-semibold">Accettiamo animali domestici</div>
                                    <div className="text-xs text-gray-400 mt-0.5">
                                        Il cliente vedrà il badge <span className={form.pets_allowed ? "text-emerald-300" : "text-rose-400"}>{form.pets_allowed ? '"Accettati"' : '"Non accettati"'}</span> sulla pagina di prenotazione.
                                    </div>
                                </div>
                            </div>
                            <input
                                type="checkbox"
                                checked={!!form.pets_allowed}
                                onChange={(e) => setForm({ ...form, pets_allowed: e.target.checked })}
                                className="w-5 h-5 accent-emerald-400"
                                data-testid="settings-pets-checkbox"
                            />
                        </label>

                        <label className="mt-3 flex items-center justify-between gap-4 border border-white/10 rounded-sm px-4 py-3 cursor-pointer hover:border-emerald-400/40" data-testid="settings-gluten-toggle">
                            <div className="flex items-center gap-3">
                                <Wheat size={20} className={form.gluten_free ? "text-emerald-300" : "text-gray-500"} strokeWidth={1.5} />
                                <div>
                                    <div className="text-white text-sm font-semibold">Ristorante attrezzato gluten free</div>
                                    <div className="text-xs text-gray-400 mt-0.5">
                                        Quando attivo, il badge verde <span className="text-emerald-300 font-semibold">"Attrezzato gluten free"</span> apparirà sotto il nome del ristorante sulla scheda pubblica.
                                    </div>
                                </div>
                            </div>
                            <input
                                type="checkbox"
                                checked={!!form.gluten_free}
                                onChange={(e) => setForm({ ...form, gluten_free: e.target.checked })}
                                className="w-5 h-5 accent-emerald-400"
                                data-testid="settings-gluten-checkbox"
                            />
                        </label>
                    </div>

                    <button type="submit" disabled={saving}
                        className="flex items-center gap-2 bg-gold text-black font-semibold px-6 py-3 rounded-sm hover:bg-gold-light tracking-widest uppercase text-sm disabled:opacity-50"
                        data-testid="settings-save-btn">
                        <Save size={16} /> {saving ? "Salvataggio..." : "Salva impostazioni"}
                    </button>
                </form>

                <PrivateMirrorSection />
                <GoogleBusinessLinkSection slug={slug} />
                <BillingInfoSection />
                <SubscriptionsSection />
            </main>
        </div>
    );
}

function SubscriptionsSection() {
    const [plans, setPlans] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyPlan, setBusyPlan] = useState(null);

    const load = async () => {
        try {
            const r = await api.get("/admin/subscriptions");
            setPlans(r.data.plans || {});
        } catch {
            // silent — likely admin not yet onboarded
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    // After Stripe redirect (?sub=success&plan=xxx) → sync state from Stripe.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const sub = params.get("sub");
        const plan = params.get("plan");
        if (sub === "success" && plan) {
            (async () => {
                try {
                    await api.post(`/admin/subscriptions/${plan}/sync`);
                    toast.success("Abbonamento attivato");
                    await load();
                } catch {
                    toast.error("Pagamento ricevuto, sincronizzazione in corso...");
                }
                window.history.replaceState({}, "", window.location.pathname);
            })();
        } else if (sub === "cancel") {
            toast.info("Pagamento annullato");
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, []);

    const startCheckout = async (planKey) => {
        setBusyPlan(planKey);
        try {
            const r = await api.post("/admin/subscriptions/checkout", {
                plan: planKey,
                origin_url: window.location.origin,
            });
            if (r.data?.url) {
                window.location.href = r.data.url;
            } else {
                toast.error("URL Stripe non disponibile");
                setBusyPlan(null);
            }
        } catch (err) {
            const detail = err.response?.data?.detail;
            // 422 con `code: billing_info_required` → guida l'utente al form
            // di fatturazione che è obbligatorio prima del checkout.
            if (err.response?.status === 422 && detail && typeof detail === "object" && detail.code === "billing_info_required") {
                toast.error(detail.message || "Compila prima i dati di fatturazione");
                setBusyPlan(null);
                // Scroll al form fatturazione
                setTimeout(() => {
                    const el = document.querySelector('[data-testid="billing-info-section"]');
                    if (el) {
                        el.scrollIntoView({ behavior: "smooth", block: "start" });
                        el.classList.add("ring-2", "ring-amber-400");
                        setTimeout(() => el.classList.remove("ring-2", "ring-amber-400"), 2500);
                    }
                }, 100);
                return;
            }
            toast.error(typeof detail === "string" ? detail : "Errore durante l'avvio del pagamento");
            setBusyPlan(null);
        }
    };

    const cancelSub = async (planKey) => {
        if (!window.confirm("Confermi la disdetta? L'abbonamento resterà attivo fino alla fine del periodo già pagato.")) return;
        setBusyPlan(planKey);
        try {
            await api.post(`/admin/subscriptions/${planKey}/cancel`);
            toast.success("Disdetta programmata — attivo fino a fine periodo");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setBusyPlan(null);
        }
    };

    const resumeSub = async (planKey) => {
        setBusyPlan(planKey);
        try {
            await api.post(`/admin/subscriptions/${planKey}/resume`);
            toast.success("Disdetta annullata — abbonamento ripristinato");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setBusyPlan(null);
        }
    };

    if (loading) return null;
    if (!plans) return null;

    const orderedKeys = ["mirror", "pro"];

    return (
        <section className="mt-12 max-w-3xl" data-testid="subscriptions-section">
            <div className="flex items-center gap-3 mb-2">
                <Crown size={18} className="text-gold" strokeWidth={1.5} />
                <span className="text-gold text-xs tracking-[0.4em] uppercase">Abbonamenti</span>
            </div>
            <h2 className="font-serif text-3xl text-white mb-2">Potenzia il tuo Prenova</h2>
            <p className="text-sm text-gray-400 mb-6 leading-relaxed">
                Sblocca funzioni premium per il tuo ristorante. Pagamento mensile sicuro tramite Stripe.
                Puoi disdire in qualsiasi momento con un click — il piano resta attivo fino a fine periodo già pagato.
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
                {orderedKeys.map((key) => {
                    const p = plans[key];
                    if (!p) return null;
                    return (
                        <PlanCard
                            key={key}
                            planKey={key}
                            plan={p}
                            busy={busyPlan === key}
                            onSubscribe={() => startCheckout(key)}
                            onCancel={() => cancelSub(key)}
                            onResume={() => resumeSub(key)}
                        />
                    );
                })}
            </div>

            <div className="mt-4 text-[11px] text-gray-500 leading-relaxed">
                I pagamenti sono gestiti da Stripe in modalità live. Ricevi una ricevuta via email ad ogni rinnovo.
                La disdetta è effettiva alla fine del ciclo di fatturazione corrente, senza penali.
            </div>
        </section>
    );
}

function PlanCard({ planKey, plan, busy, onSubscribe, onCancel, onResume }) {
    const isPro = planKey === "pro";
    const price = (plan.price_cents / 100).toFixed(2).replace(".", ",");
    const active = !!plan.active;
    const cancelling = !!plan.cancel_at_period_end;
    const manualGrant = !!plan.manual_grant;
    const trialDays = Number(plan.trial_days || 0);
    const trialAvailable = trialDays > 0 && !plan.trial_used && !manualGrant;
    const inTrial = plan.status === "trialing";
    const periodEnd = plan.current_period_end
        ? new Date(plan.current_period_end * 1000).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })
        : null;
    const trialEnd = plan.trial_end
        ? new Date(plan.trial_end * 1000).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })
        : null;

    const features = isPro ? [
        "Tutto il piano Private Mirror",
        "Rimuove \"Powered by Prenova by Renova360\"",
        "Logo del ristorante più grande",
        "Esperienza 100% white-label",
    ] : [
        "Link privato dedicato (/m/...)",
        "Solo il tuo ristorante in pagina",
        "Prenotazioni guest senza login Google",
        "Logo personalizzato in homepage",
    ];

    return (
        <div
            className={`relative rounded-sm border p-6 flex flex-col bg-ink-surface ${
                isPro ? "border-gold/60 bg-gradient-to-br from-gold/[0.06] to-transparent" : "border-white/10"
            }`}
            data-testid={`plan-card-${planKey}`}
        >
            {isPro && (
                <div className="absolute -top-2 right-4 bg-gold text-black text-[9px] font-semibold tracking-[0.3em] uppercase px-2 py-0.5 rounded-sm">
                    Top Pick
                </div>
            )}
            {!isPro && trialAvailable && !active && (
                <div className="absolute -top-2 right-4 bg-emerald-400 text-black text-[9px] font-semibold tracking-[0.3em] uppercase px-2 py-0.5 rounded-sm" data-testid={`plan-trial-badge-${planKey}`}>
                    {trialDays} giorni gratis
                </div>
            )}
            {active && (
                <div className="absolute -top-2 left-4 bg-emerald-500 text-black text-[9px] font-semibold tracking-[0.3em] uppercase px-2 py-0.5 rounded-sm" data-testid={`plan-badge-active-${planKey}`}>
                    {manualGrant ? "Cortesia" : (inTrial ? "In prova" : "Attivo")}
                </div>
            )}

            <div className="flex items-start gap-3">
                {isPro ? (
                    <Crown size={20} className="text-gold mt-1" strokeWidth={1.5} />
                ) : (
                    <Sparkles size={20} className="text-gold mt-1" strokeWidth={1.5} />
                )}
                <div className="flex-1">
                    <h3 className="font-serif text-2xl text-white">{plan.name}</h3>
                    <div className="mt-1 flex items-baseline gap-1">
                        <span className="text-3xl font-semibold text-white" data-testid={`plan-price-${planKey}`}>€{price}</span>
                        <span className="text-xs text-gray-400">/mese</span>
                    </div>
                    {trialAvailable && !active && (
                        <div className="mt-1 text-[11px] text-emerald-300" data-testid={`plan-trial-hint-${planKey}`}>
                            {trialDays} giorni di prova · nessun addebito iniziale · disdici quando vuoi
                        </div>
                    )}
                </div>
            </div>

            <p className="text-xs text-gray-400 mt-3 leading-relaxed">{plan.description}</p>

            <ul className="mt-5 space-y-2 flex-1">
                {features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-200">
                        <Check size={14} className={isPro ? "text-gold mt-0.5 shrink-0" : "text-emerald-400 mt-0.5 shrink-0"} strokeWidth={2} />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>

            <div className="mt-6 pt-5 border-t border-white/5 space-y-2">
                {!active && (
                    <button
                        onClick={onSubscribe}
                        disabled={busy}
                        className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-sm text-sm font-semibold uppercase tracking-widest disabled:opacity-50 ${
                            isPro ? "bg-gold text-black hover:bg-gold-light" : "bg-white text-black hover:bg-gray-100"
                        }`}
                        data-testid={`plan-subscribe-btn-${planKey}`}
                    >
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <ExternalLink size={14} />}
                        {busy ? "Apertura..." : (trialAvailable ? `Prova ${trialDays} giorni gratis` : "Attiva ora")}
                    </button>
                )}

                {active && manualGrant && (
                    <div
                        className="text-center px-4 py-3 rounded-sm border border-emerald-400/30 bg-emerald-500/5"
                        data-testid={`plan-courtesy-${planKey}`}
                    >
                        <div className="text-[10px] uppercase tracking-[0.3em] text-emerald-300 font-semibold">Piano cortesia Prenova by Renova360</div>
                        <div className="text-[11px] text-gray-300 mt-1">
                            Sempre attivo · nessun addebito
                        </div>
                    </div>
                )}

                {active && !manualGrant && !cancelling && (
                    <>
                        {inTrial && trialEnd && (
                            <div className="text-[11px] text-emerald-300 text-center" data-testid={`plan-trial-end-${planKey}`}>
                                Prova gratuita fino al <span className="font-semibold">{trialEnd}</span>
                            </div>
                        )}
                        {!inTrial && periodEnd && (
                            <div className="text-[11px] text-gray-400 text-center">
                                Si rinnova il <span className="text-white">{periodEnd}</span>
                            </div>
                        )}
                        <button
                            onClick={onCancel}
                            disabled={busy}
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-xs font-semibold uppercase tracking-widest border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 disabled:opacity-50"
                            data-testid={`plan-cancel-btn-${planKey}`}
                        >
                            {busy ? <Loader2 size={13} className="animate-spin" /> : <X size={13} />}
                            Disdici
                        </button>
                    </>
                )}

                {active && !manualGrant && cancelling && (
                    <>
                        <div className="text-[11px] text-amber-300 text-center" data-testid={`plan-cancelling-${planKey}`}>
                            Disdetta programmata
                            {periodEnd && <> — attivo fino al <span className="font-semibold">{periodEnd}</span></>}
                        </div>
                        <button
                            onClick={onResume}
                            disabled={busy}
                            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-sm text-xs font-semibold uppercase tracking-widest border border-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
                            data-testid={`plan-resume-btn-${planKey}`}
                        >
                            {busy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
                            Annulla disdetta
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

function PrivateMirrorSection() {
    const [pm, setPm] = useState({ enabled: false, token: null, logo_path: null });
    const [loading, setLoading] = useState(true);
    const [toggling, setToggling] = useState(false);
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        api.get("/admin/private-mirror")
            .then((r) => setPm(r.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const mirrorUrl = pm.token
        ? `${window.location.origin}/m/${pm.token}`
        : null;

    const toggle = async () => {
        setToggling(true);
        try {
            const r = await api.post("/admin/private-mirror", { enabled: !pm.enabled });
            setPm((prev) => ({ ...prev, ...r.data }));
            toast.success(r.data.enabled ? "Private Mirror attivato" : "Private Mirror disattivato");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore");
        } finally {
            setToggling(false);
        }
    };

    const copyLink = async () => {
        if (!mirrorUrl) return;
        try {
            await navigator.clipboard.writeText(mirrorUrl);
            toast.success("Link copiato negli appunti");
        } catch {
            toast.error("Copia non riuscita — copia manualmente");
        }
    };

    const uploadLogo = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            toast.error("Logo troppo grande (max 2 MB)");
            return;
        }
        const fd = new FormData();
        fd.append("file", file);
        setUploading(true);
        try {
            const r = await api.post("/admin/upload-logo", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setPm((prev) => ({ ...prev, logo_path: r.data.path }));
            toast.success("Logo aggiornato");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Upload fallito");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    if (loading) return null;

    return (
        <section className="mt-12 bg-white/[0.03] border border-white/10 rounded-sm p-7 max-w-3xl" data-testid="private-mirror-section">
            <div className="flex items-start gap-3 flex-wrap">
                <div className="h-10 w-10 flex items-center justify-center rounded-sm bg-gold/15">
                    <Sparkles size={18} className="text-gold" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-3 flex-wrap">
                        <h2 className="font-serif text-2xl text-white">Private Mirror</h2>
                        <span className="text-[9px] uppercase tracking-[0.3em] bg-gold/15 border border-gold/40 text-gold px-2 py-0.5 rounded-sm">Nuovo</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                        <strong className="text-white">A cosa serve:</strong> oltre alla tua scheda nel portale Prenova by Renova360,
                        ottieni un <strong className="text-gold">link privato dedicato</strong> alla tua sola pagina —
                        senza nessun altro ristorante della rete e con il <strong className="text-gold">tuo logo</strong>.
                        Pubblicalo sul tuo sito, sui social o nei QR code: i tuoi clienti vedranno solo il tuo
                        ristorante e potranno prenotare <strong className="text-emerald-300">senza account Google</strong>,
                        compilando solo i loro dati. Il tuo ristorante resta comunque visibile anche nel portale pubblico
                        (dove invece il login resta richiesto).
                    </p>
                </div>
            </div>

            {/* Toggle */}
            <label className="mt-6 flex items-center justify-between gap-4 border border-white/10 rounded-sm px-4 py-3 cursor-pointer hover:border-gold/40" data-testid="pm-toggle-row">
                <div>
                    <div className="text-white text-sm font-semibold">Attiva Private Mirror</div>
                    <div className="text-xs text-gray-400 mt-0.5">
                        {pm.enabled
                            ? <>Pagina pubblica disponibile su <span className="text-emerald-300">{mirrorUrl}</span></>
                            : "Quando attivo, riceverai un link permanente da condividere"}
                    </div>
                </div>
                <input
                    type="checkbox"
                    checked={!!pm.enabled}
                    disabled={toggling}
                    onChange={toggle}
                    className="w-5 h-5 accent-gold"
                    data-testid="pm-toggle-checkbox"
                />
            </label>

            {pm.enabled && pm.token && (
                <div className="mt-5 grid sm:grid-cols-[1fr_auto_auto] gap-2 items-stretch" data-testid="pm-link-row">
                    <div className="relative">
                        <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 text-gold" size={14} strokeWidth={1.5} />
                        <input
                            readOnly
                            value={mirrorUrl}
                            className="w-full pl-9 pr-3 py-2.5 bg-black/30 border border-white/15 rounded-sm text-sm text-emerald-300 font-mono"
                            data-testid="pm-link-input"
                            onFocus={(e) => e.target.select()}
                        />
                    </div>
                    <button onClick={copyLink}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 border border-gold/40 text-gold hover:bg-gold/10 rounded-sm text-xs uppercase tracking-widest"
                        data-testid="pm-copy-btn">
                        <Copy size={13} /> Copia
                    </button>
                    <a href={mirrorUrl} target="_blank" rel="noreferrer"
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 bg-gold text-black hover:bg-gold-light rounded-sm text-xs uppercase tracking-widest font-semibold"
                        data-testid="pm-open-btn">
                        <ExternalLink size={13} /> Apri
                    </a>
                </div>
            )}

            {/* Logo upload */}
            <div className="mt-6 border-t border-white/5 pt-5">
                <div className="text-[10px] uppercase tracking-[0.3em] text-gray-400 mb-3">Logo del ristorante</div>
                <div className="flex items-center gap-4 flex-wrap">
                    <div className="w-20 h-20 rounded-sm border border-white/10 bg-black/30 flex items-center justify-center overflow-hidden" data-testid="pm-logo-preview">
                        {pm.logo_path ? (
                            <img src={`${API}/files/${pm.logo_path}`} alt="logo" className="w-full h-full object-contain p-2" />
                        ) : (
                            <ImageIcon size={22} className="text-gray-600" strokeWidth={1.5} />
                        )}
                    </div>
                    <div>
                        <label className="inline-flex items-center gap-2 cursor-pointer bg-white/5 hover:bg-white/10 border border-white/15 px-4 py-2 rounded-sm text-xs uppercase tracking-widest text-gray-300">
                            <Upload size={13} />
                            {uploading ? "Caricamento..." : pm.logo_path ? "Cambia logo" : "Carica logo"}
                            <input type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml"
                                onChange={uploadLogo} disabled={uploading}
                                className="hidden"
                                data-testid="pm-logo-upload" />
                        </label>
                        <div className="text-[10px] text-gray-500 mt-2">JPG · PNG · WEBP · SVG · max 2 MB · meglio quadrato</div>
                    </div>
                </div>
            </div>

            {!pm.enabled && (
                <div className="mt-5 flex items-start gap-2 text-[11px] text-gray-500" data-testid="pm-hint-disabled">
                    <EyeOff size={13} className="mt-0.5 shrink-0" />
                    <span>Quando il Private Mirror è disattivato il link non è raggiungibile, ma resta salvato — riattivandolo, lo stesso URL torna live.</span>
                </div>
            )}
        </section>
    );
}

function GoogleBusinessLinkSection({ slug }) {
    const [token, setToken] = useState(null);
    const [mirrorEnabled, setMirrorEnabled] = useState(false);
    const [mode, setMode] = useState("public"); // "public" | "mirror"

    useEffect(() => {
        api.get("/admin/private-mirror")
            .then((r) => {
                setToken(r.data?.token || null);
                setMirrorEnabled(!!r.data?.enabled);
            })
            .catch(() => {});
    }, []);

    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const publicUrl = slug ? `${origin}/r/${slug}` : "";
    const mirrorUrl = token ? `${origin}/m/${token}` : "";
    const chosenUrl = mode === "mirror" ? mirrorUrl : publicUrl;

    const copyLink = async () => {
        if (!chosenUrl) return;
        try {
            await navigator.clipboard.writeText(chosenUrl);
            toast.success("Link copiato negli appunti");
        } catch {
            toast.error("Copia non riuscita — copia manualmente");
        }
    };

    return (
        <section className="mt-12 bg-white/[0.03] border border-white/10 rounded-sm p-7 max-w-3xl" data-testid="google-business-section">
            <div className="flex items-start gap-3 flex-wrap">
                <div className="h-10 w-10 flex items-center justify-center rounded-sm bg-blue-500/15">
                    <MapPin size={18} className="text-blue-300" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-[260px]">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-blue-300/80 font-semibold">Google Business Profile</div>
                    <h2 className="font-serif text-2xl text-white mt-0.5">Pubblica il link prenotazioni su Google Business</h2>
                    <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                        Aggiungi il link delle prenotazioni Prenova direttamente sulla scheda Google del tuo ristorante, così i clienti possono prenotare con un click dalla ricerca o da Google Maps.
                    </p>
                </div>
            </div>

            {/* Mode selector */}
            <div className="mt-6 grid sm:grid-cols-2 gap-3" data-testid="gb-mode-selector">
                <button
                    type="button"
                    onClick={() => setMode("public")}
                    className={`text-left p-4 rounded-sm border transition-colors ${mode === "public" ? "bg-gold/10 border-gold/60" : "bg-ink border-white/10 hover:border-white/30"}`}
                    data-testid="gb-mode-public"
                >
                    <div className="flex items-center gap-2">
                        <span className={`inline-block w-3 h-3 rounded-full border ${mode === "public" ? "bg-gold border-gold" : "border-gray-500"}`} />
                        <span className="text-sm font-semibold text-white">Pagina pubblica Prenova360</span>
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1.5">Il tuo ristorante visibile a tutti, anche tramite ricerca su Prenova360.</p>
                </button>
                <button
                    type="button"
                    onClick={() => setMode("mirror")}
                    disabled={!token}
                    className={`text-left p-4 rounded-sm border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${mode === "mirror" ? "bg-gold/10 border-gold/60" : "bg-ink border-white/10 hover:border-white/30"}`}
                    data-testid="gb-mode-mirror"
                    title={token ? "" : "Private Mirror non ancora generato — attivalo nel riquadro sopra"}
                >
                    <div className="flex items-center gap-2">
                        <span className={`inline-block w-3 h-3 rounded-full border ${mode === "mirror" ? "bg-gold border-gold" : "border-gray-500"}`} />
                        <span className="text-sm font-semibold text-white">Private Mirror</span>
                        {!mirrorEnabled && token && (
                            <span className="text-[9px] uppercase tracking-widest text-amber-300/80">Disattivato</span>
                        )}
                    </div>
                    <p className="text-[11px] text-gray-400 mt-1.5">Pagina dedicata, senza branding Prenova360. Tienila attiva nelle impostazioni sopra.</p>
                </button>
            </div>

            {/* Selected URL */}
            <div className="mt-5 bg-ink border border-white/10 rounded-sm p-4" data-testid="gb-link-card">
                <div className="text-[10px] uppercase tracking-[0.3em] text-gray-500 mb-1.5">Link da incollare su Google Business</div>
                <div className="flex items-center gap-3 flex-wrap">
                    <code className="flex-1 min-w-0 text-gold font-mono text-sm break-all" data-testid="gb-link-value">
                        {chosenUrl || "—"}
                    </code>
                    <div className="flex gap-2 shrink-0">
                        <button
                            onClick={copyLink}
                            disabled={!chosenUrl}
                            className="inline-flex items-center gap-1.5 text-xs text-blue-300 border border-blue-400/40 hover:bg-blue-500/10 px-3 py-2 rounded-sm tracking-widest uppercase font-semibold disabled:opacity-50"
                            data-testid="gb-copy-btn"
                        >
                            <Copy size={12} /> Copia
                        </button>
                        {chosenUrl && (
                            <a
                                href={chosenUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1.5 text-xs text-gray-300 border border-white/20 hover:bg-white/5 px-3 py-2 rounded-sm tracking-widest uppercase font-semibold"
                                data-testid="gb-preview-btn"
                            >
                                <ExternalLink size={12} /> Anteprima
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* Step-by-step instructions */}
            <ol className="mt-6 space-y-4" data-testid="gb-steps">
                <li className="flex gap-4">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gold/15 text-gold border border-gold/40 flex items-center justify-center text-xs font-semibold">1</span>
                    <p className="text-sm text-gray-200 leading-relaxed">
                        Sulla scheda del tuo Google Business, clicca <strong className="text-white">«Modifica profilo»</strong> (o <em>«Edit profile»</em>).
                    </p>
                </li>
                <li className="flex gap-4">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gold/15 text-gold border border-gold/40 flex items-center justify-center text-xs font-semibold">2</span>
                    <p className="text-sm text-gray-200 leading-relaxed">
                        Cerca la sezione <strong className="text-white">«Prenotazioni»</strong> (<em>Reservation</em>) o <strong className="text-white">«Link per Prenotare»</strong> e incolla qui sopra il link che vedi nel riquadro (puoi scegliere tra <strong className="text-white">pagina pubblica Prenova360</strong> o <strong className="text-white">Private Mirror</strong>).
                    </p>
                </li>
                <li className="flex gap-4">
                    <span className="flex-shrink-0 w-7 h-7 rounded-full bg-gold/15 text-gold border border-gold/40 flex items-center justify-center text-xs font-semibold">3</span>
                    <p className="text-sm text-gray-200 leading-relaxed">
                        Clicca <strong className="text-white">«Salva»</strong> (o <em>«Save»</em>). Dopo qualche minuto Google mostrerà il pulsante <strong className="text-emerald-300">«Prenota»</strong> sulla tua scheda pubblica.
                    </p>
                </li>
            </ol>

            <div className="mt-5 text-[11px] text-gray-500 leading-relaxed flex gap-2 items-start">
                <Info size={12} className="text-gold/70 mt-0.5 flex-shrink-0" />
                <span>
                    Se non vedi la sezione «Prenotazioni» su Google, attiva prima la categoria <em>Ristorante</em> nelle impostazioni del profilo. Google approva il link in genere entro 24 ore.
                </span>
            </div>
        </section>
    );
}


function BillingInfoSection() {
    const [bi, setBi] = useState({
        legal_name: "", vat_or_cf: "", address: "",
        email: "", pec_or_sdi: "", phone: "",
    });
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(false);
    const [missing, setMissing] = useState([]);
    const [updatedAt, setUpdatedAt] = useState(null);

    useEffect(() => {
        api.get("/admin/billing-info")
            .then((r) => {
                const data = r.data || {};
                const cur = data.billing_info || {};
                setBi({
                    legal_name: cur.legal_name || "",
                    vat_or_cf: cur.vat_or_cf || "",
                    address: cur.address || "",
                    email: cur.email || "",
                    pec_or_sdi: cur.pec_or_sdi || "",
                    phone: cur.phone || "",
                });
                setMissing(data.missing_fields || []);
                setUpdatedAt(cur.updated_at || null);
            })
            .catch(() => {})
            .finally(() => setLoaded(true));
    }, []);

    const save = async (e) => {
        e?.preventDefault();
        setSaving(true);
        try {
            const payload = { ...bi };
            // Auto-fill standard placeholder per PEC/SDI come da requisito
            if (!payload.pec_or_sdi?.trim()) payload.pec_or_sdi = "0000000";
            const r = await api.put("/admin/billing-info", payload);
            setUpdatedAt(r.data?.billing_info?.updated_at || new Date().toISOString());
            setMissing([]);
            toast.success("Dati fatturazione salvati");
        } catch (err) {
            const det = err.response?.data?.detail;
            if (Array.isArray(det)) {
                toast.error(det.map((d) => `${d.loc?.[1] || ""}: ${d.msg}`).join(" · "));
            } else {
                toast.error(det || "Impossibile salvare");
            }
        } finally {
            setSaving(false);
        }
    };

    const isComplete = missing.length === 0 && updatedAt;

    return (
        <section className="mt-12 bg-white/[0.03] border border-white/10 rounded-sm p-7 max-w-3xl transition-all" data-testid="billing-info-section">
            <div className="flex items-start gap-3 flex-wrap">
                <div className="h-10 w-10 flex items-center justify-center rounded-sm bg-amber-500/15">
                    <FileText size={18} className="text-amber-300" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] uppercase tracking-[0.3em] text-amber-300/80 font-semibold">Fatturazione</span>
                        {loaded && (isComplete
                            ? <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-300 border border-emerald-500/30">Completi</span>
                            : <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-300 border border-amber-500/30">Da compilare</span>
                        )}
                    </div>
                    <h2 className="font-serif text-2xl text-white mt-0.5">Dati per la fatturazione</h2>
                    <p className="text-xs text-gray-400 mt-2 leading-relaxed">
                        Obbligatori prima di attivare un piano a pagamento (alla fine del periodo di prova). Verranno usati per emettere le fatture elettroniche. In assenza sia di PEC che di Codice SDI, lascia <strong className="text-amber-200">0000000</strong>.
                    </p>
                </div>
            </div>

            <form onSubmit={save} className="mt-6 grid sm:grid-cols-2 gap-4">
                <BFld label="Ragione sociale / Nome e Cognome" required testid="bi-legal-name">
                    <input value={bi.legal_name} onChange={(e) => setBi({ ...bi, legal_name: e.target.value })}
                        data-testid="bi-legal-name-input"
                        placeholder="Es. Trattoria del Vicolo SRL"
                        className="w-full px-3 py-2.5 bg-ink border border-white/15 rounded-sm text-white focus:border-amber-400/60 outline-none" />
                </BFld>
                <BFld label="P.IVA o Codice Fiscale" required testid="bi-vat-cf">
                    <input value={bi.vat_or_cf} onChange={(e) => setBi({ ...bi, vat_or_cf: e.target.value.toUpperCase() })}
                        data-testid="bi-vat-cf-input"
                        placeholder="IT12345678901 o RSSMRA80A01F205Z"
                        className="w-full px-3 py-2.5 bg-ink border border-white/15 rounded-sm text-white focus:border-amber-400/60 outline-none font-mono" />
                </BFld>
                <BFld label="Indirizzo (sede)" required full testid="bi-address">
                    <input value={bi.address} onChange={(e) => setBi({ ...bi, address: e.target.value })}
                        data-testid="bi-address-input"
                        placeholder="Via Roma 1, 20100 Milano (MI)"
                        className="w-full px-3 py-2.5 bg-ink border border-white/15 rounded-sm text-white focus:border-amber-400/60 outline-none" />
                </BFld>
                <BFld label="Email fatturazione" required testid="bi-email">
                    <input type="email" value={bi.email} onChange={(e) => setBi({ ...bi, email: e.target.value })}
                        data-testid="bi-email-input"
                        placeholder="amministrazione@miristorante.it"
                        className="w-full px-3 py-2.5 bg-ink border border-white/15 rounded-sm text-white focus:border-amber-400/60 outline-none" />
                </BFld>
                <BFld label="PEC o Codice SDI" required testid="bi-pec-sdi">
                    <input value={bi.pec_or_sdi} onChange={(e) => setBi({ ...bi, pec_or_sdi: e.target.value })}
                        data-testid="bi-pec-sdi-input"
                        placeholder="miopec@pec.it o 0000000"
                        className="w-full px-3 py-2.5 bg-ink border border-white/15 rounded-sm text-white focus:border-amber-400/60 outline-none font-mono" />
                    <div className="text-[10px] text-gray-500 mt-1">Se non hai né PEC né Codice SDI, lascia <strong className="text-amber-300">0000000</strong></div>
                </BFld>
                <BFld label="Telefono" required testid="bi-phone">
                    <input value={bi.phone} onChange={(e) => setBi({ ...bi, phone: e.target.value })}
                        data-testid="bi-phone-input"
                        placeholder="+39 02 12345678"
                        className="w-full px-3 py-2.5 bg-ink border border-white/15 rounded-sm text-white focus:border-amber-400/60 outline-none" />
                </BFld>

                <div className="sm:col-span-2 flex items-center justify-between gap-3 flex-wrap mt-2">
                    <div className="text-[11px] text-gray-500">
                        {updatedAt && <>Ultimo aggiornamento: {new Date(updatedAt).toLocaleString("it-IT")}</>}
                    </div>
                    <button type="submit" disabled={saving}
                        className="inline-flex items-center gap-2 bg-amber-400 text-ink px-6 py-2.5 rounded-sm tracking-widest uppercase text-xs font-semibold hover:bg-amber-300 disabled:opacity-50"
                        data-testid="bi-save-btn">
                        <Save size={13} /> {saving ? "Salvo…" : "Salva dati fatturazione"}
                    </button>
                </div>
            </form>
        </section>
    );
}


function BFld({ label, required, full, children, testid }) {
    return (
        <label className={`${full ? "sm:col-span-2" : ""} block`} data-testid={`${testid}-wrap`}>
            <span className="block text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold mb-1.5">
                {label}{required && <span className="text-amber-300 ml-1">*</span>}
            </span>
            {children}
        </label>
    );
}


function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.3em] text-gray-400 font-semibold">{label}</span>
            <div className="mt-2">{children}</div>
        </label>
    );
}
